import {
  applySkillAward,
  applyOfflineTourFinale,
  etWeekKey,
  SKILL_TOUR_PODIUM_BONUS,
  type SkillAward,
} from '@/core/skillPoints';
import { cosmeticById } from '@/config/cosmetics';
import { CITIES } from '@/config/cities';
import { isVehicleUnlocked } from '@/core/Progression';
import {
  GUEST_POT_CAP,
  PENDING_AWARDS_CAP,
  PENDING_PURCHASES_CAP,
  parseSave,
  type CityRecord,
  type PendingAward,
  type SaveData,
  type VehicleLoadout,
} from '@/core/SaveData';

/**
 * accountLogic.ts — the PURE rules of the RACER ID economy (M100). No network,
 * no supabase-js — just save-in/save-out transforms, so every rule here is
 * unit-tested. AccountService is the impure shell that calls the server.
 *
 * THE ROUTING RULE (owner spec 2026-07-23 — "no SP until the account is
 * created; motivate it in the flow"):
 *   1. Account layer disabled (kill switch) → exactly pre-M100: award the
 *      local save.
 *   2. Device LINKED to a Racer ID → award the local save (the offline cache)
 *      AND queue the event in the outbox. It posts whenever a session is
 *      live — an expired session or a dead connection never loses points.
 *   3. BIG KID + unlinked → award the local save only. Kids can't create
 *      accounts (and Big Kid never advertises them); their garage progression
 *      keeps working on the device.
 *   4. Otherwise (guest, normal modes) → the award goes to the GUEST POT
 *      (career untouched, nothing unlocks). The results screen shows the
 *      would-be payout + CREATE YOUR RACER ID; signing in claims the pot.
 *
 * Server mirrors (keep in sync with the Supabase migration `racer_id_v1`):
 * guest claims are capped at GUEST_CLAIM_MAX per ET day; the one-time local
 * import is capped at IMPORT_MAX and only lands in a FRESH wallet.
 */

export const GUEST_CLAIM_MAX = 300;
export const IMPORT_MAX = 10000;

export type AwardRoute = 'account' | 'local' | 'guest' | 'none';

export interface AwardMeta {
  /** Idempotency key — the server's dedupe handle (see Flow's id scheme). */
  id: string;
  kind: PendingAward['kind'];
  /** Finish place (race) or championship place (finales), 1-based. */
  place: number;
}

export interface BankResult {
  save: SaveData;
  /** Vehicle ids this banking unlocked (empty on guest/none routes). */
  newlyUnlocked: string[];
  route: AwardRoute;
  /** The award as it should be PRESENTED (guest routes still show it). */
  award: SkillAward;
}

/** Where an award would land for this save right now. */
export function routeForAward(save: SaveData, enabled: boolean): AwardRoute {
  if (!enabled) return 'local';
  if (save.account) return 'account';
  if (save.settings.difficulty === 'bigkid') return 'local';
  return 'guest';
}

/** Queue an outbox entry (bounded — beyond the cap the oldest post first and
 *  the newest is dropped with its points already in the local cache; the next
 *  reconcile trues the totals up against the server). */
function pushPending(save: SaveData, entry: PendingAward): SaveData {
  const next = structuredClone(save);
  if (next.pendingAwards.length < PENDING_AWARDS_CAP) next.pendingAwards.push(entry);
  return next;
}

/**
 * Bank one RACE (or online tour finale) award by the routing rule. The one
 * entry point Flow calls for every award EXCEPT the offline tour finale
 * (which also tracks its highest tier paid — see bankOfflineFinale).
 */
export function bankAward(
  save: SaveData,
  award: SkillAward,
  meta: AwardMeta,
  enabled: boolean,
  now: Date = new Date(),
): BankResult {
  if (award.total <= 0) return { save, newlyUnlocked: [], route: 'none', award };
  const route = routeForAward(save, enabled);
  if (route === 'guest') {
    const next = structuredClone(save);
    next.guestPoints = Math.min(GUEST_POT_CAP, next.guestPoints + award.total);
    return { save: next, newlyUnlocked: [], route, award };
  }
  const applied = applySkillAward(save, award, now);
  if (route === 'account') {
    const queued = pushPending(applied.save, {
      id: meta.id,
      kind: meta.kind,
      place: meta.place,
      points: award.total,
      week: etWeekKey(now),
    });
    return { save: queued, newlyUnlocked: applied.newlyUnlocked, route, award };
  }
  return { save: applied.save, newlyUnlocked: applied.newlyUnlocked, route, award };
}

/**
 * Bank the OFFLINE tour finale by the routing rule. Owner rule (2026-10-02):
 * every top-3 championship pays its FULL bonus, every time (the M79 "offline
 * pays once" high-water rule is retired — some players prefer offline). On a
 * Racer ID it posts as its own `tour_finale_offline` ledger event (unique id
 * per tour), which is also what the dashboard's TROPHY CABINET counts; a
 * guest's bonus goes to the guest pot, whose cap + daily claim limit still
 * bound what signed-out play can bank. `offlineTourBonusPaid` is kept only as
 * the highest tier ever paid.
 */
export function bankOfflineFinale(
  save: SaveData,
  championshipPlace: number,
  eventId: string,
  enabled: boolean,
  now: Date = new Date(),
): BankResult {
  const route = routeForAward(save, enabled);
  if (route === 'guest') {
    const tier = SKILL_TOUR_PODIUM_BONUS[championshipPlace] ?? 0;
    const award: SkillAward = {
      reason: 'tour-finale',
      base: 0,
      podiumBonus: 0,
      tourBonus: tier,
      total: tier,
    };
    if (tier <= 0) return { save, newlyUnlocked: [], route: 'none', award };
    const next = structuredClone(save);
    next.guestPoints = Math.min(GUEST_POT_CAP, next.guestPoints + tier);
    next.offlineTourBonusPaid = Math.max(save.offlineTourBonusPaid, tier);
    return { save: next, newlyUnlocked: [], route, award };
  }
  const res = applyOfflineTourFinale(save, championshipPlace);
  if (res.award.total <= 0) {
    return { save: res.save, newlyUnlocked: [], route: 'none', award: res.award };
  }
  if (route === 'account') {
    const queued = pushPending(res.save, {
      id: eventId,
      kind: 'tour_finale_offline',
      place: championshipPlace,
      points: res.award.total,
      week: etWeekKey(now),
    });
    return { save: queued, newlyUnlocked: res.newlyUnlocked, route, award: res.award };
  }
  return { save: res.save, newlyUnlocked: res.newlyUnlocked, route, award: res.award };
}

/** Queue a shop purchase for posting (linked saves; idempotent by item id). */
export function queuePurchase(save: SaveData, itemId: string): SaveData {
  if (!save.account) return save;
  if (save.pendingPurchases.includes(itemId)) return save;
  const next = structuredClone(save);
  if (next.pendingPurchases.length < PENDING_PURCHASES_CAP) next.pendingPurchases.push(itemId);
  return next;
}

// ---------------------------------------------------------------------------
// Reconcile — the server is the wallet authority; the local save is the
// offline cache. After every sync: local = server + whatever is still queued.
// ---------------------------------------------------------------------------

export interface ServerWallet {
  total: number;
  spent: number;
  weekly: number;
  finalePaidOffline: number;
  nickname: string;
  /** M130 — SP bought with money (profiles.purchased_points). Optional so a
   *  wallet from a not-yet-migrated server (deploy skew) leaves the device's
   *  mirror alone instead of zeroing a paid balance. */
  purchased?: number;
}

/** M109 — the ACCOUNT's garage as the server holds it (profiles.loadouts +
 *  player_vehicle), plus whether any device has ever pushed one. */
export interface ServerGarage {
  loadouts: Record<string, VehicleLoadout>;
  /** The account's selected ride, or null if it has never been set. */
  playerVehicle: string | null;
  /** false = set_garage has NEVER run for this account. */
  everSet: boolean;
}

/** What a sync should do with the garage this round. */
export type GarageAction = 'push' | 'adopt' | 'none';

/**
 * M109 — the cross-device garage rule, in one pure function.
 *
 * Equipped cosmetics used to be device-local, so the same Racer ID showed
 * different paint on every device (the owner's report). The garage is
 * server-side now; this decides which side wins on a given sync:
 *
 *   • a DIFFERENT user signed in  → ADOPT theirs, wholesale (never inherit the
 *     previous account's garage — the M103 rule, applied to cosmetics)
 *   • this device changed something (`garageDirty`) → PUSH it up
 *   • the account has never had a garage → PUSH (seed it from this device, so
 *     the update can't wipe what players already have equipped)
 *   • otherwise → ADOPT the account's garage
 *
 * Deliberately last-write-wins: two devices editing the same garage converge
 * on whoever synced last, which is what "my garage, everywhere" means in
 * practice and needs no clocks or merge rules.
 */
export function garageAction(save: SaveData, garage: ServerGarage, adopt: boolean): GarageAction {
  if (adopt) return 'adopt';
  const localEmpty = Object.keys(save.cosmetics.loadouts).length === 0;
  const serverEmpty = Object.keys(garage.loadouts).length === 0;
  // THE EMPTY-GARAGE RULE. A device wearing nothing must never overwrite an
  // account that is wearing something: that is what a fresh install, a
  // RESET ALL PROGRESS, and a device that hasn't synced yet all look like, and
  // getting it wrong costs the player their paint on EVERY device at once.
  // The price is small and self-correcting: un-equipping your very last item
  // everywhere doesn't sync until you next equip something.
  if (localEmpty && !serverEmpty) return 'adopt';
  if (save.garageDirty) return 'push';
  // Seeding a never-synced account from an EMPTY device would set garage_set
  // and make every other device adopt that emptiness — the same wipe by
  // another route. Wait for a device that actually has something to seed with.
  if (!garage.everSet) return localEmpty ? 'none' : 'push';
  return 'adopt';
}

/**
 * Fold the account's garage into the save (the 'adopt' branch). The result
 * runs through parseSave, so the usual invariants re-apply: only OWNED items
 * stay equipped, unknown ids and unknown vehicle keys are dropped, and the
 * saved ride is always one the player has unlocked.
 *
 * `playerVehicle` only moves if the server actually has one AND the player has
 * unlocked it — otherwise this device keeps the ride it is already on rather
 * than being bounced onto a car it cannot drive.
 */
export function adoptGarage(save: SaveData, garage: ServerGarage): SaveData {
  const next = structuredClone(save);
  next.cosmetics.loadouts = structuredClone(garage.loadouts);
  if (garage.playerVehicle && isVehicleUnlocked(next, garage.playerVehicle)) {
    next.settings.playerVehicle = garage.playerVehicle;
  }
  next.garageDirty = false;
  return parseSave(JSON.stringify(next));
}

/** Mark the local garage as ahead of the account (call on any real change). */
export function markGarageDirty(save: SaveData): SaveData {
  if (save.garageDirty) return save;
  const next = structuredClone(save);
  next.garageDirty = true;
  return next;
}

/**
 * The push landed — this device is level with the account again.
 *
 * Deliberately does NOT adopt the server's echo. `set_garage` validates
 * ownership against the `purchases` ledger, while the local owned-set is a
 * legitimate SUPERSET of it (reconcileWallet keeps locally-owned items that
 * have no purchase row — anything bought while signed out, for instance). So
 * adopting the echo would silently un-equip an item the player still owns, and
 * re-strip it on every sync after that: an item you cannot keep on your car.
 * The local save is already validated by parseSave, so keeping it is safe; the
 * server copy is what OTHER devices adopt, and it only ever carries items the
 * account can prove.
 */
export function clearGarageDirty(save: SaveData): SaveData {
  if (!save.garageDirty) return save;
  const next = structuredClone(save);
  next.garageDirty = false;
  return next;
}

/**
 * Fold the server's wallet + owned items into the save. `adopt` = wholesale
 * adoption (a DIFFERENT user signed in on this device, or a fresh-reset save
 * re-linked): local-only wallet state is discarded in favour of the server's.
 * Runs the result through parseSave so every existing invariant (spent ≤
 * career, loadouts only wear owned items, ride always unlocked) re-applies.
 */
/**
 * M103 — is `uid` a DIFFERENT Racer ID than this device last carried?
 * `account` alone can't answer it: sign-out nulls the link, so a
 * sign-out → different-user sign-in used to read as a fresh link and leaked
 * the previous account's local state (nickname, cosmetics) into the new one
 * — the "phone shows the other account's name" bug. `lastAccountId`
 * survives sign-out precisely so this check works across the gap.
 */
export function isUserSwitch(save: SaveData, uid: string): boolean {
  if (save.account) return save.account.userId !== uid;
  return save.lastAccountId !== null && save.lastAccountId !== uid;
}

export function reconcileWallet(
  save: SaveData,
  userId: string,
  wallet: ServerWallet,
  serverOwned: string[],
  adopt: boolean,
  now: Date = new Date(),
): SaveData {
  const next = structuredClone(save);
  next.account = { userId };
  next.lastAccountId = userId;
  const pending = adopt ? [] : next.pendingAwards;
  const pendingPurchases = adopt ? [] : next.pendingPurchases;
  next.pendingAwards = pending;
  next.pendingPurchases = pendingPurchases;

  const week = etWeekKey(now);
  const pendingPts = pending.reduce((s, a) => s + a.points, 0);
  const pendingWeekPts = pending.reduce((s, a) => (a.week === week ? s + a.points : s), 0);
  const pendingSpent = pendingPurchases.reduce((s, id) => s + (cosmeticById(id)?.price ?? 0), 0);

  next.lifetimePoints = Math.max(0, wallet.total + pendingPts);
  next.spentPoints = Math.max(0, wallet.spent + pendingSpent);
  // M130: bought SP is SERVER-authoritative with no client outbox — the only
  // writers are the payment webhooks. Adopt it whenever the wallet carries it
  // (a user switch included: it is that account's money). A guest device's
  // value is always 0, so nothing can leak between accounts here.
  if (typeof wallet.purchased === 'number' && Number.isFinite(wallet.purchased)) {
    next.purchasedPoints = Math.max(0, Math.floor(wallet.purchased));
  }
  next.weekly = { key: week, points: Math.max(0, wallet.weekly + pendingWeekPts) };
  next.offlineTourBonusPaid = adopt
    ? wallet.finalePaidOffline
    : Math.max(next.offlineTourBonusPaid, wallet.finalePaidOffline);
  const owned = new Set<string>([...serverOwned, ...pendingPurchases]);
  if (!adopt) for (const id of next.cosmetics.owned) owned.add(id);
  next.cosmetics.owned = [...owned];
  // Nickname: the SAVE only ever holds what the PLAYER typed — the server's
  // generated handle stays server-side (display surfaces read it from the
  // account state instead). Writing it here once polluted a later sign-in
  // with a DIFFERENT account (the adopted handle pushed onto the new
  // profile — measured in the M100 E2E). A user switch adopts the new
  // account's handle wholesale: that identity, this device.
  if (adopt) next.nickname = (wallet.nickname ?? '').slice(0, 12);
  // M114: the grandfathered-vehicle grants (save.unlockedVehicles) belong to
  // the DEVICE'S ORIGINAL career, not the account signing in — a user switch
  // must not hand a fresh account a veteran's legacy unlocks (the M103
  // never-leak rule). Cleared BEFORE parseSave so the ride coercion re-runs
  // against the new account's real career total.
  if (adopt) next.unlockedVehicles = [];

  // parseSave is the single validation boundary — reuse it so the reconciled
  // save honours every invariant the rest of the game already relies on.
  return parseSave(JSON.stringify(next));
}

// ---------------------------------------------------------------------------
// M110 — CITY RECORDS follow the account (owner: "personal times across all
// devices"). The merge is MONOTONIC on every field, mirroring the server's
// merge_city_records exactly: any device can push at any time, replays are
// no-ops, and every device converges with no clocks or dirty flags.
// ---------------------------------------------------------------------------

/** Fold the server's merged city records into the local set. Only KNOWN city
 *  ids survive (the server stores by shape, the client validates by catalog),
 *  and each field takes the BEST of the two sides: unlocked only turns on,
 *  best place only improves, best time only drops, raced only grows. */
export function mergeCityRecords(
  local: Record<string, CityRecord>,
  server: unknown,
): Record<string, CityRecord> {
  const known = new Set(CITIES.map((c) => c.id));
  const out: Record<string, CityRecord> = {};
  const srv = (server && typeof server === 'object' ? server : {}) as Record<string, unknown>;
  for (const id of known) {
    const l = local[id];
    const raw = srv[id] as Partial<CityRecord> | undefined;
    const s: CityRecord | null =
      raw && typeof raw === 'object'
        ? {
            unlocked: raw.unlocked === true,
            bestPlace:
              typeof raw.bestPlace === 'number' && Number.isInteger(raw.bestPlace)
                ? Math.min(8, Math.max(0, raw.bestPlace))
                : 0,
            bestTimeMs:
              typeof raw.bestTimeMs === 'number' &&
              Number.isFinite(raw.bestTimeMs) &&
              raw.bestTimeMs > 0
                ? Math.round(raw.bestTimeMs)
                : null,
            raced:
              typeof raw.raced === 'number' && Number.isFinite(raw.raced)
                ? Math.min(1_000_000, Math.max(0, Math.round(raw.raced)))
                : 0,
          }
        : null;
    if (!l && !s) continue;
    if (!l) {
      out[id] = s as CityRecord;
      continue;
    }
    if (!s) {
      out[id] = { ...l };
      continue;
    }
    out[id] = {
      unlocked: l.unlocked || s.unlocked,
      bestPlace:
        l.bestPlace === 0
          ? s.bestPlace
          : s.bestPlace === 0
            ? l.bestPlace
            : Math.min(l.bestPlace, s.bestPlace),
      bestTimeMs:
        l.bestTimeMs === null
          ? s.bestTimeMs
          : s.bestTimeMs === null
            ? l.bestTimeMs
            : Math.min(l.bestTimeMs, s.bestTimeMs),
      raced: Math.max(l.raced, s.raced),
    };
  }
  return out;
}

/** The guest-pot claim for this sign-in: how much to ask the server for now.
 *  (The server allows one claim per ET day, capped — the rest stays in the
 *  pot and claims on a later day.) */
export function claimAmount(save: SaveData): number {
  return Math.min(save.guestPoints, GUEST_CLAIM_MAX);
}

/** Apply a completed claim: the server awarded `awarded` (possibly 0 if the
 *  daily claim was already used) — only that much leaves the pot. */
export function applyClaim(save: SaveData, awarded: number): SaveData {
  const next = structuredClone(save);
  next.guestPoints = Math.max(0, next.guestPoints - Math.max(0, awarded));
  return next;
}

/** The one-time import request for a fresh wallet (0 = nothing to import). */
export function importAmount(save: SaveData): number {
  if (save.accountImported) return 0;
  return Math.min(Math.max(0, Math.round(save.lifetimePoints)), IMPORT_MAX);
}

/**
 * M101 — has this DEVICE seen real play? The onboarding grandfather rule
 * (owner: "strictly for new users"): a brand-new account whose device already
 * carries progress — a converting web guest, a veteran signing in — skips the
 * post-signup onboarding silently (Flow marks the account onboarded
 * server-side instead of fronting the flow). A tour counts even with zero
 * results: starting one means they've seen the menus work.
 */
export function hasMeaningfulProgress(save: SaveData): boolean {
  return (
    save.lifetimePoints > 0 ||
    save.guestPoints > 0 ||
    save.tour !== null ||
    Object.keys(save.cities).length > 0
  );
}
